import { useRef, useState } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';

interface Props {
  onNav: (
    t:
      | 'accounts'
      | 'scenarios'
      | 'investments'
      | 'assumptions'
      | 'history'
      | 'privacy'
      | 'glossary',
  ) => void;
}

export function SettingsPage({ onNav }: Props) {
  const {
    activeProfile,
    profiles,
    switchProfile,
    createProfile,
    deleteProfile,
    duplicateProfile,
    resetProfileToDemo,
    exportJson,
    importJson,
    resetAll,
  } = useStore();

  const fileRef = useRef<HTMLInputElement>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTemplate, setNewTemplate] = useState<'seed' | 'demo' | 'blank'>('seed');
  const [duplicatingFrom, setDuplicatingFrom] = useState<string | null>(null);
  const [duplicateName, setDuplicateName] = useState('');

  const list = profiles.length > 0
    ? profiles
    : [{ name: activeProfile, mtime: 0 }];

  async function handleSwitch(name: string) {
    if (name === activeProfile) return;
    await switchProfile(name);
    setMsg(`Switched to "${name}".`);
  }

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    if (!isValidProfileName(name)) {
      setMsg('Profile name: letters, digits, dash, underscore. Max 60 chars.');
      return;
    }
    if (profiles.some(p => p.name === name)) {
      setMsg(`A profile named "${name}" already exists.`);
      return;
    }
    try {
      await createProfile(name, newTemplate);
      setMsg(`Created profile "${name}".`);
      setCreating(false);
      setNewName('');
      setNewTemplate('seed');
    } catch (e) {
      setMsg(`Create failed: ${(e as Error).message}`);
    }
  }

  async function handleDuplicate() {
    if (!duplicatingFrom) return;
    const to = duplicateName.trim();
    if (!to || !isValidProfileName(to)) {
      setMsg('Profile name: letters, digits, dash, underscore. Max 60 chars.');
      return;
    }
    if (profiles.some(p => p.name === to)) {
      setMsg(`A profile named "${to}" already exists.`);
      return;
    }
    try {
      await duplicateProfile(duplicatingFrom, to);
      setMsg(`Duplicated "${duplicatingFrom}" → "${to}".`);
      setDuplicatingFrom(null);
      setDuplicateName('');
    } catch (e) {
      setMsg(`Duplicate failed: ${(e as Error).message}`);
    }
  }

  async function handleDelete(name: string) {
    if (!confirm(`Delete profile "${name}"? This cannot be undone.`)) return;
    try {
      await deleteProfile(name);
      setMsg(`Deleted profile "${name}".`);
    } catch (e) {
      setMsg(`Delete failed: ${(e as Error).message}`);
    }
  }

  async function handleResetDemo(name: string) {
    if (!confirm(`Reset "${name}" to the canonical demo dataset? Any edits in this profile will be lost.`)) return;
    try {
      await resetProfileToDemo(name);
      setMsg(`Reset "${name}" to demo seed.`);
    } catch (e) {
      setMsg(`Reset failed: ${(e as Error).message}`);
    }
  }

  async function handleExport() {
    const json = await exportJson();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cairn-${activeProfile}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setMsg(`Exported "${activeProfile}" to Downloads.`);
  }

  async function handleImport(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      await importJson(text);
      setMsg(`Imported into "${activeProfile}".`);
    } catch (e) {
      setMsg(`Import failed: ${(e as Error).message}`);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleReset() {
    if (!confirm('Erase ALL data in the active profile and restore seed? This can’t be undone.')) return;
    await resetAll();
    setMsg(`Reset "${activeProfile}" to seed.`);
  }

  return (
    <>
      <TopBar title="More" subtitle="Manage data & settings" />
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-4 pb-nav">
        {/* Profiles */}
        <section className="card card-dark p-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <div>
              <h3 className="text-sm font-semibold">Profiles</h3>
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-300">
                Each profile is its own dataset. Real data stays on this machine; nothing is committed or sent anywhere.
              </p>
            </div>
            {!creating && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="btn btn-ghost text-xs"
              >
                + New
              </button>
            )}
          </div>

          {creating && (
            <div className="rounded-xl border border-ink-200 bg-ink-50/50 p-3 dark:border-ink-700 dark:bg-ink-800/40">
              <label className="label text-[11px]">Name</label>
              <input
                className="input mt-1"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. work-scenario"
                autoFocus
              />
              <div className="mt-3">
                <label className="label text-[11px]">Initial data</label>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {(['seed', 'demo', 'blank'] as const).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setNewTemplate(t)}
                      className={`rounded-md border p-2 text-xs ${
                        newTemplate === t
                          ? 'border-accent-500 bg-accent-50 dark:bg-accent-700/20'
                          : 'border-ink-200 dark:border-ink-700'
                      }`}
                    >
                      <div className="font-semibold capitalize">{t}</div>
                      <div className="text-[10px] text-ink-500 dark:text-ink-300">
                        {t === 'seed'
                          ? 'starter values'
                          : t === 'demo'
                            ? 'fictional sample'
                            : 'empty'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button className="btn btn-primary flex-1 text-xs" onClick={handleCreate}>
                  Create
                </button>
                <button
                  className="btn btn-ghost flex-1 text-xs"
                  onClick={() => { setCreating(false); setNewName(''); setNewTemplate('seed'); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <ul className="space-y-2">
            {list.map(p => (
              <li
                key={p.name}
                className={`rounded-xl border p-3 ${
                  p.name === activeProfile
                    ? 'border-accent-500 bg-accent-50/50 dark:bg-accent-700/10'
                    : 'border-ink-200 dark:border-ink-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-semibold">
                      {p.name === activeProfile && <span className="text-accent-600">●</span>}
                      {p.name}
                      {p.name === 'demo' && (
                        <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
                          demo
                        </span>
                      )}
                    </div>
                    {p.name === activeProfile && (
                      <div className="mt-0.5 text-[11px] text-ink-500 dark:text-ink-300">Active</div>
                    )}
                  </div>
                  {p.name !== activeProfile && (
                    <button
                      type="button"
                      onClick={() => handleSwitch(p.name)}
                      className="btn btn-ghost text-[11px]"
                    >
                      Switch
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { setDuplicatingFrom(p.name); setDuplicateName(`${p.name}-copy`); }}
                    className="btn btn-ghost text-[11px]"
                  >
                    Duplicate
                  </button>
                  {p.name === 'demo' && (
                    <button
                      type="button"
                      onClick={() => handleResetDemo(p.name)}
                      className="btn btn-ghost text-[11px]"
                      title="Wipe and reseed from the canonical demo dataset"
                    >
                      Reset
                    </button>
                  )}
                  {list.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleDelete(p.name)}
                      className="btn btn-ghost text-[11px] text-neg-700 dark:text-neg-500"
                    >
                      Delete
                    </button>
                  )}
                </div>
                {duplicatingFrom === p.name && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      className="input flex-1"
                      value={duplicateName}
                      onChange={e => setDuplicateName(e.target.value)}
                      placeholder="new profile name"
                      autoFocus
                    />
                    <button className="btn btn-primary text-xs" onClick={handleDuplicate}>
                      Copy
                    </button>
                    <button
                      className="btn btn-ghost text-xs"
                      onClick={() => { setDuplicatingFrom(null); setDuplicateName(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section className="card card-dark">
          <NavRow label="Accounts" hint="Add, edit, archive accounts" onClick={() => onNav('accounts')} />
          <NavRow label="Investments" hint="Contribution rules, IRS cap awareness, per-account view" onClick={() => onNav('investments')} />
          <NavRow label="Assumptions" hint="Ages, returns, mortgage, spending" onClick={() => onNav('assumptions')} />
          <NavRow label="Scenarios" hint="Plans and life events that drive the projection" onClick={() => onNav('scenarios')} />
          <NavRow label="History" hint="Snapshots over time" onClick={() => onNav('history')} />
          <NavRow label="Glossary" hint="Plain-language definitions of every term" onClick={() => onNav('glossary')} />
        </section>

        <section className="card card-dark p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Backup</h3>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-300">
              Export the active profile as JSON, or load JSON into the active profile. The file stays on
              your device unless you send it somewhere.
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-primary flex-1" onClick={handleExport}>
              Export JSON
            </button>
            <button className="btn btn-ghost flex-1" onClick={() => fileRef.current?.click()}>
              Import JSON
            </button>
            <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={handleImport} />
          </div>
        </section>

        <section className="card card-dark p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h3 className="text-sm font-semibold">Privacy</h3>
              <ul className="mt-1 space-y-1 text-xs text-ink-500 dark:text-ink-300">
                <li>• In dev, profiles are JSON files at <code>local-data/profiles/</code> (gitignored).</li>
                <li>• In a static build, profiles live in this browser's IndexedDB.</li>
                <li>• Nothing is ever sent to any server or third party.</li>
                <li>• A Content Security Policy blocks unexpected network calls.</li>
              </ul>
            </div>
            <button
              type="button"
              onClick={() => onNav('privacy')}
              className="btn btn-ghost text-xs"
            >
              Details →
            </button>
          </div>
        </section>

        <section className="card card-dark p-4">
          <h3 className="text-sm font-semibold text-neg-700 dark:text-neg-500">Danger zone</h3>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-300">
            Reset the active profile to seed values. Make sure you exported first if you want to keep the data.
          </p>
          <button className="btn btn-danger mt-3 w-full" onClick={handleReset}>
            Reset active profile to seed
          </button>
        </section>

        {msg && (
          <div className="rounded-xl bg-ink-900 px-4 py-3 text-xs text-white dark:bg-ink-50 dark:text-ink-900">
            {msg}
          </div>
        )}
      </main>
    </>
  );
}

function isValidProfileName(name: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9_-]{0,59}$/.test(name);
}

function NavRow({ label, hint, onClick }: { label: string; hint: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 border-b border-ink-100 px-4 py-3 text-left last:border-b-0 dark:border-ink-700"
    >
      <div className="flex-1">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-[11px] text-ink-500 dark:text-ink-300">{hint}</div>
      </div>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ink-400">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}
